import type { GameAction } from "@fate-kingdom/engine";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

const MOUSE_DRAG_THRESHOLD = 7;
const TOUCH_DRAG_THRESHOLD = 12;

export type AlphaDisbandAction = Extract<
  GameAction,
  { readonly type: "disband-army" | "disband-commander" }
>;

export interface AlphaFieldDisbandDragState {
  readonly active: boolean;
  readonly cardInstanceId: string | null;
  readonly hovered: boolean;
  readonly currentX: number;
  readonly currentY: number;
}

const IDLE_STATE: AlphaFieldDisbandDragState = {
  active: false,
  cardInstanceId: null,
  hovered: false,
  currentX: 0,
  currentY: 0,
};

interface Candidate {
  readonly action: AlphaDisbandAction;
  readonly cardInstanceId: string;
  readonly pointerId: number;
  readonly pointerType: string;
  readonly startX: number;
  readonly startY: number;
  readonly trigger: HTMLButtonElement;
  dragging: boolean;
}

function wantsDisbandDrag(pointerType: string, dx: number, dy: number): boolean {
  const threshold = pointerType === "mouse" ? MOUSE_DRAG_THRESHOLD : TOUCH_DRAG_THRESHOLD;
  return dy >= threshold && Math.abs(dy) > Math.abs(dx);
}

/** Presentation-only Pointer Events adapter for exact legal field-card disband actions. */
export function useAlphaFieldDisbandDrag({
  isFriendlyDiscardAtPoint,
  onDrop,
  onCancel,
}: {
  readonly isFriendlyDiscardAtPoint: (x: number, y: number) => boolean;
  readonly onDrop: (action: AlphaDisbandAction) => void;
  readonly onCancel: (message: string) => void;
}) {
  const [state, setState] = useState<AlphaFieldDisbandDragState>(IDLE_STATE);
  const candidateRef = useRef<Candidate | null>(null);
  const callbacksRef = useRef({ isFriendlyDiscardAtPoint, onDrop, onCancel });
  const suppressClickRef = useRef(false);

  useEffect(() => {
    callbacksRef.current = { isFriendlyDiscardAtPoint, onDrop, onCancel };
  }, [isFriendlyDiscardAtPoint, onCancel, onDrop]);

  const clear = useCallback(() => {
    const candidate = candidateRef.current;
    if (candidate?.trigger.hasPointerCapture(candidate.pointerId)) {
      candidate.trigger.releasePointerCapture(candidate.pointerId);
    }
    candidateRef.current = null;
    setState(IDLE_STATE);
  }, []);

  const cancel = useCallback(
    (message = "Disband cancelled.") => {
      if (candidateRef.current?.dragging) suppressClickRef.current = true;
      if (candidateRef.current !== null || state.active) callbacksRef.current.onCancel(message);
      clear();
    },
    [clear, state.active],
  );

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || candidate.pointerId !== event.pointerId) return;
      const dx = event.clientX - candidate.startX;
      const dy = event.clientY - candidate.startY;
      if (!candidate.dragging) {
        if (!wantsDisbandDrag(candidate.pointerType, dx, dy)) return;
        candidate.dragging = true;
        if (candidate.pointerType !== "mouse") {
          candidate.trigger.setPointerCapture(candidate.pointerId);
        }
      }
      event.preventDefault();
      setState({
        active: true,
        cardInstanceId: candidate.cardInstanceId,
        hovered: callbacksRef.current.isFriendlyDiscardAtPoint(event.clientX, event.clientY),
        currentX: event.clientX,
        currentY: event.clientY,
      });
    };
    const up = (event: PointerEvent) => {
      const candidate = candidateRef.current;
      if (candidate === null || candidate.pointerId !== event.pointerId) return;
      if (!candidate.dragging) return clear();
      suppressClickRef.current = true;
      const accepted = callbacksRef.current.isFriendlyDiscardAtPoint(event.clientX, event.clientY);
      const action = candidate.action;
      clear();
      if (accepted) callbacksRef.current.onDrop(action);
      else callbacksRef.current.onCancel("Release on your discard pile to disband this card.");
    };
    const cancelled = () => cancel();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") cancel();
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", up, { passive: false });
    window.addEventListener("pointercancel", cancelled);
    window.addEventListener("blur", cancelled);
    window.addEventListener("keydown", keydown);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancelled);
      window.removeEventListener("blur", cancelled);
      window.removeEventListener("keydown", keydown);
    };
  }, [cancel, clear]);

  const beginPress = useCallback(
    (
      actionsByCardId: ReadonlyMap<string, AlphaDisbandAction>,
      event: ReactPointerEvent<HTMLButtonElement>,
    ): boolean => {
      if (event.button !== 0) return false;
      const target =
        event.target instanceof Element
          ? event.target.closest<HTMLElement>("[data-alpha-field-card-id]")
          : null;
      const cardInstanceId = target?.dataset.alphaFieldCardId;
      const action = cardInstanceId === undefined ? undefined : actionsByCardId.get(cardInstanceId);
      if (cardInstanceId === undefined || action === undefined) return false;
      candidateRef.current = {
        action,
        cardInstanceId,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        trigger: event.currentTarget,
        dragging: false,
      };
      return true;
    },
    [],
  );

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return { state, beginPress, cancel, consumeSuppressedClick };
}
